import React, { useMemo, useState } from 'react';
import Text from '@/components/ui/Text';
import { toast } from '@/components/ui/Toast';
import cn from '@/utils/cn';
import { useLiveSessionDetail } from '../LiveSessionsMonitor/hooks';
import { LoadingSkeleton } from '../LiveSessionsMonitor/tableShared';
import { useRunAnalysis, useSessionAnalysis } from './analysisHooks';
import AnalysisPanel from './AnalysisPanel';
import { deriveParticipantStates } from './deriveParticipantStates';
import { catalogFor } from './eventCatalog';
import FilterBar, { type ActorOption } from './FilterBar';
import HeaderCard from './HeaderCard';
import { useSessionEvents, useSessionParticipants } from './hooks';
import SlideSyncTrack from './SlideSyncTrack';
import StudentLanes from './StudentLanes';
import Timeline from './Timeline';
import {
  actorKeyFor, DEFAULT_FILTERS, studentActorKey, SYSTEM_ACTOR,
  type Filters, type LiveSessionEvent, type ParticipantsResponse,
} from './types';

interface LiveSessionForensicsProps {
  // LaunchDarkly `live_session_ai_summary`, evaluated for the sysadmin by Admin::LiveSessionsController#show.
  aiSummaryEnabled?: boolean;
  sessionId: string;
}

const EVIDENCE_NOT_LOADED_MESSAGE = 'That event is not in the loaded timeline. '
  + 'It may have been pruned or is beyond the loaded range.';

const ERROR_BANNER_CLASS = cn(
  'tw-bg-red-50 tw-border tw-border-solid tw-border-red-200 tw-text-red-700 tw-rounded-lg tw-p-4',
  'tw-flex tw-items-center tw-justify-between',
);

const RETRY_BUTTON_CLASS = cn(
  'tw-bg-white tw-border tw-border-solid tw-border-red-300 tw-text-red-700 tw-rounded tw-px-3 tw-py-1 tw-text-sm',
);

const BODY_GRID_CLASS = 'tw-grid tw-grid-cols-1 lg:tw-grid-cols-[minmax(0,2fr)_minmax(0,1fr)] tw-gap-5 tw-items-start';

const matchesQuery = (event: LiveSessionEvent, query: string) => {
  if (!query) return true;
  const haystack = `${event.event_type} ${catalogFor(event.event_type).label} ${JSON.stringify(event.metadata)}`
    .toLowerCase();
  return haystack.includes(query.toLowerCase());
};

export const applyFilters = (events: LiveSessionEvent[], filters: Filters): LiveSessionEvent[] => (
  events.filter(event => (
    (filters.actor === 'all' || actorKeyFor(event) === filters.actor)
    && (filters.source === 'all' || event.source === filters.source)
    && (filters.categories.length === 0 || filters.categories.includes(event.category))
    && matchesQuery(event, filters.query)
  ))
);

const actorOptions = (participants: ParticipantsResponse | undefined): ActorOption[] => {
  const options: ActorOption[] = [{ label: 'Everyone', value: 'all' }];
  if (participants) {
    const { teacher } = participants;
    options.push({ label: `${teacher.name ?? 'Teacher'} (teacher)`, value: `Staffer:${teacher.id}` });
    participants.students.forEach(s => options.push({ label: s.full_name, value: studentActorKey(s.id) }));
  }
  options.push({ label: 'System', value: SYSTEM_ACTOR });
  return options;
};

const EmptyLog = () => (
  <div className="tw-border tw-border-solid tw-border-blue-200 tw-bg-blue-50 tw-rounded-lg tw-p-4">
    <Text className="tw-text-tci-black tw-m-0" size="sm">
      No event log for this session. It started before event logging shipped, or logging was switched off.
      The participants list below still reflects who joined.
    </Text>
  </div>
);

const LiveSessionForensics = ({ aiSummaryEnabled = false, sessionId }: LiveSessionForensicsProps) => {
  const detailQuery = useLiveSessionDetail(sessionId);
  const isLive = detailQuery.data?.status === 'active';
  const { events, isError, isLoading, refetch } = useSessionEvents(sessionId, isLive);
  const participantsQuery = useSessionParticipants(sessionId, isLive);
  const analysisQuery = useSessionAnalysis(sessionId, aiSummaryEnabled);
  const { error: analysisError, isRunning: isAnalyzing, run: runAnalysis } = useRunAnalysis(sessionId);

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const [highlightEventId, setHighlightEventId] = useState<number | null>(null);

  const startedAt = detailQuery.data?.started_at ?? events[0]?.occurred_at ?? new Date().toISOString();
  const endedAt = detailQuery.data?.ended_at ?? null;
  const students = participantsQuery.data?.students ?? [];

  const derived = useMemo(() => deriveParticipantStates(events, {
    endedAt,
    invitedIds: new Set(students.filter(s => s.invited).map(s => s.id)),
    startedAt,
  }), [events, endedAt, startedAt, students]);

  const filtered = useMemo(() => applyFilters(events, filters), [events, filters]);
  const selectedStudent = students.find(s => s.id === selectedStudentId) ?? null;

  const selectStudent = (studentId: number | null) => {
    setSelectedStudentId(studentId);
    setFilters(prev => ({ ...prev, actor: studentId === null ? 'all' : studentActorKey(studentId) }));
  };

  const selectActor = (actor: string) => {
    setFilters(prev => ({ ...prev, actor }));
    const match = /^Student:(\d+)$/.exec(actor);
    setSelectedStudentId(match ? Number(match[1]) : null);
  };

  // Evidence must be visible to be checked, so every filter that could hide the row is cleared.
  const showEvidence = (eventId: number) => {
    if (!events.some(event => event.id === eventId)) {
      toast.show(EVIDENCE_NOT_LOADED_MESSAGE);
      return;
    }
    setFilters(DEFAULT_FILTERS);
    setSelectedStudentId(null);
    setHighlightEventId(eventId);
  };

  return (
    <div className="tw-bg-gray-50 tw-min-h-screen tw-px-6 tw-py-6">
      <div className="tw-flex tw-flex-col tw-gap-5 tw-max-w-[1400px] tw-mx-auto">
        <HeaderCard
          detail={detailQuery.data}
          eventCount={events.length}
          isLive={isLive}
          participants={participantsQuery.data}
          sessionId={sessionId}
        />

        {isError && (
          <div className={ERROR_BANNER_CLASS}>
            <span>Failed to load the event log.</span>
            <button className={RETRY_BUTTON_CLASS} type="button" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        )}

        {!isLoading && !isError && events.length === 0 && <EmptyLog />}

        <div className={BODY_GRID_CLASS}>
          <div className="tw-flex tw-flex-col tw-gap-3 tw-min-w-0">
            {aiSummaryEnabled && (
              <AnalysisPanel
                analysis={analysisQuery.data}
                error={analysisError}
                isRunning={isAnalyzing}
                students={students}
                onEvidenceClick={showEvidence}
                onRun={runAnalysis}
                onSelectStudent={selectStudent}
              />
            )}
            <SlideSyncTrack
              end={derived.end}
              events={events}
              selectedStudent={selectedStudent}
              start={derived.start}
              teacherDrops={derived.teacherDrops}
            />
            <FilterBar
              actors={actorOptions(participantsQuery.data)}
              filters={filters}
              shownCount={filtered.length}
              totalCount={events.length}
              onChange={setFilters}
            />
            {isLoading ? <LoadingSkeleton /> : (
              <Timeline
                events={filtered}
                highlightEventId={highlightEventId}
                startedAt={startedAt}
                onSelectActor={selectActor}
              />
            )}
          </div>
          <div className="tw-flex tw-flex-col tw-gap-5 tw-min-w-0">
            {participantsQuery.isLoading ? <LoadingSkeleton /> : (
              <StudentLanes
                derived={derived}
                selectedStudentId={selectedStudentId}
                students={students}
                onSelect={selectStudent}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveSessionForensics;
